// src/views/portaria/PortariaDashboard.jsx
import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useAuth } from "../../contexts/AuthContext";
import portariaService from "../../services/portariaService";
import useSocket from "../../hooks/useSocket";

const fmtDT = d => { try{ return new Date(d).toLocaleString("pt-BR",{day:"2-digit",month:"2-digit",hour:"2-digit",minute:"2-digit"}); }catch{return"—";} };

// "há 2h15min" — útil para o porteiro saber há quanto tempo a pessoa está dentro.
const tempoDentro = (dt) => {
  if (!dt) return "—";
  const ms = Date.now() - new Date(dt).getTime();
  if (ms < 0 || isNaN(ms)) return "—";
  const min = Math.floor(ms/60000);
  if (min < 1)  return "agora";
  if (min < 60) return `${min}min`;
  const h = Math.floor(min/60);
  const r = min % 60;
  if (h < 24) return r > 0 ? `${h}h ${r}min` : `${h}h`;
  const d = Math.floor(h/24);
  return `${d}d ${h % 24}h`;
};

const COR_GRAV = { Alta:"#991B1B", Media:"#854D0E", Baixa:"#166534" };
const BG_GRAV  = { Alta:"#FEE2E2", Media:"#FEF9C3", Baixa:"#DCFCE7" };
const COR_TP   = { Visitante:"#1D4ED8", Prestador:"#7C3AED", Familiar:"#166534", Entrega:"#C2410C", Outro:"#64748B" };
const BG_TP    = { Visitante:"#EFF6FF", Prestador:"#F5F3FF", Familiar:"#DCFCE7", Entrega:"#FFF7ED", Outro:"#F1F5F9" };

export default function PortariaDashboard({ defaultAba }) {
  const { condominioAtivo } = useAuth();
  const idCondo = condominioAtivo?.id;
  const condoNome = condominioAtivo?.nmFantasia || condominioAtivo?.nmCondominio || "";
  const [dados,       setDados]       = useState(null);
  const [registros,   setRegistros]   = useState([]);
  const [encomendas,  setEncomendas]  = useState([]);
  const [ocorrencias, setOcorrencias] = useState([]);
  const [loading,     setLoading]     = useState(true);
  const [aba,         setAba]         = useState(defaultAba||"movimento");
  const [relatorio,   setRelatorio]   = useState(null);
  const [relLoading,  setRelLoading]  = useState(false);
  const [relDtIni,    setRelDtIni]    = useState(new Date().toISOString().split("T")[0]);
  const [relDtFim,    setRelDtFim]    = useState(new Date().toISOString().split("T")[0]);
  // Filtros por tab
  const [movFiltro,   setMovFiltro]   = useState("dentro");  // dentro | todos | saidos
  const [movDtIni,    setMovDtIni]    = useState(() => { const d=new Date(); d.setDate(d.getDate()-7); return d.toISOString().split("T")[0]; });
  const [movDtFim,    setMovDtFim]    = useState(new Date().toISOString().split("T")[0]);
  const [encFiltro,   setEncFiltro]   = useState("Aguardando"); // Aguardando | "" | Retirada
  const [encDtIni2,   setEncDtIni2]   = useState(() => { const d=new Date(); d.setDate(d.getDate()-30); return d.toISOString().split("T")[0]; });
  const [encDtFim2,   setEncDtFim2]   = useState(new Date().toISOString().split("T")[0]);
  const [ocoFiltro,   setOcoFiltro]   = useState(""); // "" | Aberta | Encaminhada | Resolvida
  const [ocoDtIni,    setOcoDtIni]    = useState(() => { const d=new Date(); d.setDate(d.getDate()-30); return d.toISOString().split("T")[0]; });
  const [ocoDtFim,    setOcoDtFim]    = useState(new Date().toISOString().split("T")[0]);
  const [encRelatorio,setEncRelatorio]= useState(null);
  const [encLoading,  setEncLoading]  = useState(false);
  const [encDtIni,    setEncDtIni]    = useState(new Date().toISOString().split("T")[0]);
  const [encDtFim,    setEncDtFim]    = useState(new Date().toISOString().split("T")[0]);

  const carregar = useCallback(async () => {
    if (!idCondo) return;
    setLoading(true);
    try {
      const [dash, regs, encs, ocos] = await Promise.all([
        portariaService.dashboard({idCondominio:idCondo}),
        portariaService.registros({idCondominio:idCondo, status:"dentro", limit:20}),
        portariaService.encomendas({idCondominio:idCondo, status:"Aguardando"}),
        portariaService.ocorrenciasPortaria({idCondominio:idCondo}),
      ]);
      setDados(dash.data||{});
      setRegistros(regs.data||[]);
      setEncomendas(encs.data||[]);
      setOcorrencias(ocos.data||[]);
    } catch(e) { console.error(e); }
    setLoading(false);
  }, [idCondo]);

  useEffect(() => { carregar(); }, [carregar]);

  // Auto-carrega o histórico do dia ao abrir a tab pela primeira vez
  useEffect(() => {
    if (aba === "historico" && !relatorio && !relLoading && idCondo) {
      carregarRelatorio();
    }
  }, [aba, idCondo]); // eslint-disable-line

  // Memoiza para evitar recriar o array filtrado a cada render.
  const ocorrenciasAlta = useMemo(
    () => ocorrencias.filter(o => o.gravidade === "Alta"),
    [ocorrencias]
  );

  const recarregarMovimento = async (filtro, dtIni, dtFim) => {
    const params = {idCondominio:idCondo};
    if (filtro==="dentro") params.status="dentro";
    else if (filtro==="saidos") params.status="saiu";
    if (filtro!=="dentro") { params.dtIni=dtIni; params.dtFim=dtFim; }
    else params.limit=50;
    const r = await portariaService.registros(params);
    setRegistros(r.data||[]);
  };

  const recarregarEncomendas = async (filtro, dtIni, dtFim) => {
    const params = {idCondominio:idCondo, dtIni, dtFim};
    if (filtro) params.status=filtro;
    const r = await portariaService.encomendas(params);
    setEncomendas(r.data||[]);
  };

  const recarregarOcorrencias = async (filtro, dtIni, dtFim) => {
    const params = {idCondominio:idCondo, dtIni, dtFim};
    if (filtro) params.status=filtro;
    const r = await portariaService.ocorrenciasPortaria(params);
    setOcorrencias(r.data||[]);
  };

  // Real-time updates
  useSocket(idCondo, {
    visita_chegou:      () => carregar(),
    encomenda_chegou:   () => carregar(),
    ocorrencia_portaria:(data) => {
      if (data.gravidade === "Alta") {
        // Flash the badge
        carregar();
      } else {
        carregar();
      }
    },
  });


  const carregarRelatorio = async () => {
    if (!idCondo) return;
    setRelLoading(true);
    try {
      const r = await portariaService.relatorio({idCondominio:idCondo, dtIni:relDtIni, dtFim:relDtFim});
      setRelatorio(r.data);
    } catch(e){ console.error(e); }
    setRelLoading(false);
  };

  const carregarEncRelatorio = async () => {
    if (!idCondo) return;
    setEncLoading(true);
    try {
      const r = await portariaService.encomendas({idCondominio:idCondo,
        dtIni:encDtIni, dtFim:encDtFim, status:""});
      setEncRelatorio(r.data||[]);
    } catch(e){ console.error(e); }
    setEncLoading(false);
  };

  if (!idCondo) return (
    <div style={{textAlign:"center",padding:60,color:"var(--gray-400)"}}>
      <i className="bi bi-buildings" style={{fontSize:40,display:"block",marginBottom:12}}/>
      Selecione um condomínio para ver o painel da portaria
    </div>
  );

  // Badges usam dados do dashboard (backend) para refletir a realidade do condomínio,
  // mesmo que filtros temporários alterem a contagem das listas exibidas.
  const badgeEnc = dados?.encomendasAguard ?? encomendas.length;
  const badgeOco = dados?.ocorrenciasAbertas ?? ocorrencias.length;
  const TABS = [
    { id:"movimento",    label:"Movimento", icon:"bi-door-open" },
    { id:"encomendas",   label:`Encomendas${badgeEnc>0?` (${badgeEnc})`:""}`, icon:"bi-box-seam" },
    { id:"ocorrencias",  label:`Ocorrências${badgeOco>0?` (${badgeOco})`:""}`, icon:"bi-exclamation-triangle" },
    { id:"historico",    label:"Histórico", icon:"bi-graph-up" },
    { id:"enc_relatorio",label:"Rel. Encomendas", icon:"bi-box-seam" },
  ];

  return (
    <div>
      <div className="page-header">
        <div>
          <h1 className="page-title">
            <i className="bi bi-shield-check me-2" style={{color:"var(--primary)",fontSize:22}}/>
            Dashboard da Portaria
          </h1>
          <p className="page-subtitle">
            {condoNome && <><strong>{condoNome}</strong> — </>}
            Monitoramento em tempo real do acesso ao condomínio
          </p>
        </div>
        <button className="btn btn-outline-secondary btn-sm" onClick={carregar}>
          <i className="bi bi-arrow-clockwise me-1"/>Atualizar
        </button>
      </div>

      {loading ? (
        <div style={{textAlign:"center",padding:60}}>
          <span className="spinner-border text-primary"/>
        </div>
      ) : (
        <>
          {/* KPI Cards */}
          <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(180px,1fr))",gap:14,marginBottom:24}}>
            {[
              { label:"Dentro agora",         value:dados?.dentroAgora||0,        icon:"bi-people-fill",         color:"#166534", bg:"#DCFCE7", tab:"movimento" },
              { label:"Entradas hoje",        value:dados?.entradasHoje||0,       icon:"bi-door-open",           color:"#1D4ED8", bg:"#EFF6FF", tab:"movimento" },
              { label:"Veículos dentro",      value:dados?.veiculosDentro||0,     icon:"bi-car-front",           color:"#7C3AED", bg:"#F5F3FF" },
              { label:"Encomendas aguardando",value:dados?.encomendasAguard||0,   icon:"bi-box-seam",            color:"#C2410C", bg:"#FFF7ED", tab:"encomendas" },
              { label:"Pré-autorizações ativas",value:dados?.preauthHoje||0,      icon:"bi-qr-code",             color:"#0891B2", bg:"#E0F2FE" },
              { label:"Ocorrências abertas",  value:dados?.ocorrenciasAbertas ?? ocorrencias.length, icon:"bi-exclamation-triangle", color:"#991B1B", bg:"#FEE2E2", tab:"ocorrencias" },
            ].map(card=>(
              <div key={card.label} className="card"
                onClick={()=>card.tab && setAba(card.tab)}
                style={{padding:18, cursor:card.tab?"pointer":"default"}}>
                <div style={{display:"flex",alignItems:"center",gap:12}}>
                  <div style={{width:44,height:44,borderRadius:12,background:card.bg,
                    display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0}}>
                    <i className={`bi ${card.icon}`} style={{fontSize:20,color:card.color}}/>
                  </div>
                  <div>
                    <div style={{fontSize:24,fontWeight:700,color:"var(--gray-800)",lineHeight:1}}>{card.value}</div>
                    <div style={{fontSize:11.5,color:"var(--gray-400)",marginTop:2}}>{card.label}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Ocorrências de alta gravidade - alerta prominente */}
          {ocorrenciasAlta.length > 0 && (
            <div style={{background:"var(--bg-danger-soft)",border:"2px solid #FECACA",borderRadius:10,
              padding:"12px 16px",marginBottom:20,display:"flex",alignItems:"flex-start",gap:10}}>
              <i className="bi bi-exclamation-triangle-fill"
                style={{color:"#991B1B",fontSize:20,flexShrink:0,marginTop:2}}/>
              <div>
                <div style={{fontWeight:700,color:"#991B1B",fontSize:14,marginBottom:6}}>
                  ⚠️ {ocorrenciasAlta.length} ocorrência(s) de alta gravidade em aberto
                </div>
                {ocorrenciasAlta.map(o=>(
                  <div key={o.id} style={{fontSize:13,color:"#7F1D1D",marginBottom:2}}>
                    <strong>{o.tpOcorrencia}</strong> — {o.descricao?.substring(0,80)}{o.descricao?.length>80?"...":""}
                    <span style={{marginLeft:8,fontSize:11,color:"#9CA3AF"}}>{fmtDT(o.dtOcorrencia)}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Tabs */}
          <div style={{background:"var(--bg-surface)",border:"1px solid var(--gray-200)",borderRadius:"12px 12px 0 0",
            borderBottom:"none",display:"flex",overflowX:"auto"}}>
            {TABS.map(t=>(
              <button key={t.id} onClick={()=>setAba(t.id)}
                style={{border:"none",background:"none",padding:"13px 20px",cursor:"pointer",
                  color:aba===t.id?"var(--primary)":"var(--gray-500)",fontWeight:aba===t.id?700:400,
                  borderBottom:aba===t.id?"3px solid var(--primary)":"3px solid transparent",
                  fontSize:13,display:"flex",alignItems:"center",gap:6,whiteSpace:"nowrap"}}>
                <i className={`bi ${t.icon}`}/> {t.label}
              </button>
            ))}
          </div>

          {/* Tab Content */}
          <div className="card" style={{borderRadius:"0 0 12px 12px",overflow:"hidden"}}>

            {/* ── MOVIMENTO ── */}
            {aba==="movimento" && (
              <>
                <div style={{padding:"12px 18px",borderBottom:"1px solid var(--gray-100)",
                  display:"flex",gap:8,alignItems:"center",flexWrap:"wrap"}}>
                  {[{v:"dentro",l:"Dentro agora"},{v:"todos",l:"Todos"},{v:"saidos",l:"Já saíram"}].map(f=>(
                    <button key={f.v} type="button"
                      onClick={()=>{setMovFiltro(f.v);recarregarMovimento(f.v,movDtIni,movDtFim);}}
                      style={{padding:"4px 14px",borderRadius:6,border:"1px solid var(--border-color)",
                        background:movFiltro===f.v?"#1B3A6B":"#fff",color:movFiltro===f.v?"#fff":"#64748B",
                        fontWeight:600,fontSize:12,cursor:"pointer"}}>
                      {f.l}
                    </button>
                  ))}
                  {movFiltro!=="dentro" && (<>
                    <input type="date" className="form-control form-control-sm" style={{width:140}}
                      value={movDtIni} onChange={e=>{setMovDtIni(e.target.value);recarregarMovimento(movFiltro,e.target.value,movDtFim);}}/>
                    <span style={{fontSize:12,color:"var(--gray-400)"}}>até</span>
                    <input type="date" className="form-control form-control-sm" style={{width:140}}
                      value={movDtFim} onChange={e=>{setMovDtFim(e.target.value);recarregarMovimento(movFiltro,movDtIni,e.target.value);}}/>
                  </>)}
                  <span style={{marginLeft:"auto",fontSize:12,color:"var(--gray-400)"}}>
                    {registros.length} registro{registros.length!==1?"s":""}
                  </span>
                </div>
                {registros.length === 0 ? (
                  <div style={{textAlign:"center",padding:40,color:"var(--gray-400)"}}>
                    <i className="bi bi-door-open" style={{fontSize:36,display:"block",marginBottom:8}}/>
                    Nenhuma pessoa dentro do condomínio
                  </div>
                ) : (
                  <table style={{width:"100%",borderCollapse:"collapse",fontSize:13}}>
                    <thead>
                      <tr style={{background:"var(--gray-50)"}}>
                        {(movFiltro==="dentro"
                          ? ["Visitante","Tipo","Unidade","Entrada","Tempo dentro","Porteiro"]
                          : ["Visitante","Tipo","Unidade","Entrada","Saída","Porteiro"]).map(h=>(
                          <th key={h} style={{padding:"10px 16px",textAlign:"left",
                            fontWeight:600,color:"var(--gray-600)",fontSize:12}}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {registros.map((r,i)=>{
                        // Destaque visual para visitantes há muito tempo dentro (>= 4h)
                        const dtRef = r.dtEntrada ? new Date(r.dtEntrada).getTime() : null;
                        const horasDentro = dtRef && !r.dtSaida ? (Date.now()-dtRef)/3600000 : 0;
                        const alerta = !r.dtSaida && horasDentro >= 4;
                        return (
                          <tr key={r.id} style={{borderBottom:"1px solid var(--gray-100)",
                            background: alerta ? "#FFF7ED" : (i%2===0?"#fff":"var(--gray-50)")}}>
                            <td style={{padding:"10px 16px",fontWeight:600}}>{r.nmVisitante}</td>
                            <td style={{padding:"10px 16px"}}>
                              <span style={{fontSize:11,fontWeight:700,padding:"3px 10px",borderRadius:12,
                                background:BG_TP[r.tpVisita]||"#F1F5F9",
                                color:COR_TP[r.tpVisita]||"#64748B"}}>
                                {r.tpVisita}
                              </span>
                            </td>
                            <td style={{padding:"10px 16px"}}>
                              {r.nmUnidade ? `Unidade ${r.nmUnidade}${r.bloco?`/Bl.${r.bloco}`:""}` : "—"}
                            </td>
                            <td style={{padding:"10px 16px",color:"var(--gray-600)"}}>{fmtDT(r.dtEntrada)}</td>
                            {movFiltro==="dentro" ? (
                              <td style={{padding:"10px 16px",fontWeight:600,
                                color: alerta ? "#C2410C" : "var(--gray-600)"}}>
                                {alerta && <i className="bi bi-clock-history me-1"/>}
                                {tempoDentro(r.dtEntrada)}
                              </td>
                            ) : (
                              <td style={{padding:"10px 16px",color:"var(--gray-600)"}}>
                                {r.dtSaida ? fmtDT(r.dtSaida) : "—"}
                              </td>
                            )}
                            <td style={{padding:"10px 16px",color:"var(--gray-400)",fontSize:12}}>{r.nmPorteiro||"—"}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                )}
              </>
            )}

            {/* ── ENCOMENDAS ── */}
            {aba==="encomendas" && (
              <>
                <div style={{padding:"12px 18px",borderBottom:"1px solid var(--gray-100)",
                  display:"flex",gap:8,alignItems:"center",flexWrap:"wrap"}}>
                  {[{v:"Aguardando",l:"Aguardando"},{v:"",l:"Todas"},{v:"Retirada",l:"Entregues"}].map(f=>(
                    <button key={f.v} type="button"
                      onClick={()=>{setEncFiltro(f.v);recarregarEncomendas(f.v,encDtIni2,encDtFim2);}}
                      style={{padding:"4px 14px",borderRadius:6,border:"1px solid var(--border-color)",
                        background:encFiltro===f.v?"#1B3A6B":"#fff",color:encFiltro===f.v?"#fff":"#64748B",
                        fontWeight:600,fontSize:12,cursor:"pointer"}}>
                      {f.l}
                    </button>
                  ))}
                  <input type="date" className="form-control form-control-sm" style={{width:140}}
                    value={encDtIni2} onChange={e=>{setEncDtIni2(e.target.value);recarregarEncomendas(encFiltro,e.target.value,encDtFim2);}}/>
                  <span style={{fontSize:12,color:"var(--gray-400)"}}>até</span>
                  <input type="date" className="form-control form-control-sm" style={{width:140}}
                    value={encDtFim2} onChange={e=>{setEncDtFim2(e.target.value);recarregarEncomendas(encFiltro,encDtIni2,e.target.value);}}/>
                  <span style={{marginLeft:"auto",fontSize:12,color:"var(--gray-400)"}}>
                    {encomendas.length} encomenda{encomendas.length!==1?"s":""}
                  </span>
                </div>
                {encomendas.length === 0 ? (
                  <div style={{textAlign:"center",padding:40,color:"var(--gray-400)"}}>
                    <i className="bi bi-box-seam" style={{fontSize:36,display:"block",marginBottom:8}}/>
                    Nenhuma encomenda aguardando
                  </div>
                ) : (
                  <table style={{width:"100%",borderCollapse:"collapse",fontSize:13}}>
                    <thead>
                      <tr style={{background:"var(--gray-50)"}}>
                        {["Descrição","Unidade","Remetente","Chegou","Foto"].map(h=>(
                          <th key={h} style={{padding:"10px 16px",textAlign:"left",
                            fontWeight:600,color:"var(--gray-600)",fontSize:12}}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {encomendas.map((e,i)=>(
                        <tr key={e.id} style={{borderBottom:"1px solid var(--gray-100)",
                          background:i%2===0?"#fff":"var(--gray-50)"}}>
                          <td style={{padding:"10px 16px",fontWeight:600}}>{e.descricao}</td>
                          <td style={{padding:"10px 16px"}}>
                            Unidade {e.nmUnidade}{e.bloco?`/Bl.${e.bloco}`:""}
                          </td>
                          <td style={{padding:"10px 16px",color:"var(--gray-600)"}}>{e.remetente||"—"}</td>
                          <td style={{padding:"10px 16px",color:"var(--gray-600)"}}>{fmtDT(e.dtChegada)}</td>
                          <td style={{padding:"10px 16px"}}>
                            {e.fotoEncomenda ? (
                              <img src={e.fotoEncomenda} alt="enc"
                                style={{width:40,height:40,borderRadius:6,objectFit:"cover",cursor:"pointer"}}
                                onClick={()=>window.open(e.fotoEncomenda,"_blank")}/>
                            ) : <span style={{color:"var(--gray-300)"}}>—</span>}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </>
            )}

            {/* ── OCORRÊNCIAS ── */}
            {aba==="ocorrencias" && (
              <>
                <div style={{padding:"12px 18px",borderBottom:"1px solid var(--gray-100)",
                  display:"flex",gap:8,alignItems:"center",flexWrap:"wrap"}}>
                  {[{v:"",l:"Todas"},{v:"Aberta",l:"Abertas"},{v:"Encaminhada",l:"Encaminhadas"},{v:"Resolvida",l:"Resolvidas"}].map(f=>(
                    <button key={f.v} type="button"
                      onClick={()=>{setOcoFiltro(f.v);recarregarOcorrencias(f.v,ocoDtIni,ocoDtFim);}}
                      style={{padding:"4px 14px",borderRadius:6,border:"1px solid var(--border-color)",
                        background:ocoFiltro===f.v?"#1B3A6B":"#fff",color:ocoFiltro===f.v?"#fff":"#64748B",
                        fontWeight:600,fontSize:12,cursor:"pointer"}}>
                      {f.l}
                    </button>
                  ))}
                  <input type="date" className="form-control form-control-sm" style={{width:140}}
                    value={ocoDtIni} onChange={e=>{setOcoDtIni(e.target.value);recarregarOcorrencias(ocoFiltro,e.target.value,ocoDtFim);}}/>
                  <span style={{fontSize:12,color:"var(--gray-400)"}}>até</span>
                  <input type="date" className="form-control form-control-sm" style={{width:140}}
                    value={ocoDtFim} onChange={e=>{setOcoDtFim(e.target.value);recarregarOcorrencias(ocoFiltro,ocoDtIni,e.target.value);}}/>
                  <span style={{marginLeft:"auto",fontSize:12,color:"var(--gray-400)"}}>
                    {ocorrencias.length} ocorrência{ocorrencias.length!==1?"s":""}
                  </span>
                </div>
                {ocorrencias.length === 0 ? (
                  <div style={{textAlign:"center",padding:40,color:"var(--gray-400)"}}>
                    <i className="bi bi-check-circle" style={{fontSize:36,display:"block",marginBottom:8}}/>
                    Nenhuma ocorrência em aberto
                  </div>
                ) : (
                  <div style={{padding:16,display:"flex",flexDirection:"column",gap:10}}>
                    {ocorrencias.map(o=>(
                      <div key={o.id} style={{
                        border:`1px solid ${BG_GRAV[o.gravidade]||"#E2E8F0"}`,
                        borderLeft:`4px solid ${COR_GRAV[o.gravidade]||"#64748B"}`,
                        borderRadius:8,padding:"12px 16px"
                      }}>
                        <div style={{display:"flex",justifyContent:"space-between",flexWrap:"wrap",gap:8}}>
                          <div style={{flex:1}}>
                            <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:4}}>
                              <span style={{fontWeight:700,fontSize:14}}>{o.tpOcorrencia}</span>
                              <span style={{fontSize:11,fontWeight:700,padding:"2px 8px",borderRadius:10,
                                background:BG_GRAV[o.gravidade],color:COR_GRAV[o.gravidade]}}>
                                {o.gravidade}
                              </span>
                              {o.nmUnidade && (
                                <span style={{fontSize:11,color:"var(--gray-500)"}}>Un. {o.nmUnidade}</span>
                              )}
                            </div>
                            <div style={{fontSize:13,color:"var(--gray-700)",marginBottom:4}}>{o.descricao}</div>
                            <div style={{fontSize:11,color:"var(--gray-400)"}}>
                              {fmtDT(o.dtOcorrencia)} · Porteiro: {o.nmPorteiro||"—"}
                              {o.local && ` · ${o.local}`}
                            </div>
                          </div>
                          <div style={{display:"flex",gap:6,alignItems:"flex-start"}}>
                            <span style={{fontSize:11,padding:"3px 10px",borderRadius:8,
                              background:"var(--bg-warning-soft)",color:"#854D0E",fontWeight:600}}>
                              {o.status}
                            </span>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </>
            )}

            {/* ── HISTÓRICO DE MOVIMENTAÇÃO ── */}
            {aba==="historico" && (
              <>
                <div style={{padding:"14px 18px",borderBottom:"1px solid var(--gray-100)"}}>
                  <div style={{display:"flex",gap:10,alignItems:"flex-end",flexWrap:"wrap"}}>
                    <div>
                      <label style={{fontSize:12,fontWeight:600,display:"block",marginBottom:4}}>De</label>
                      <input type="date" className="form-control form-control-sm"
                        value={relDtIni} onChange={e=>setRelDtIni(e.target.value)}/>
                    </div>
                    <div>
                      <label style={{fontSize:12,fontWeight:600,display:"block",marginBottom:4}}>Até</label>
                      <input type="date" className="form-control form-control-sm"
                        value={relDtFim} onChange={e=>setRelDtFim(e.target.value)}/>
                    </div>
                    <button className="btn btn-primary btn-sm" onClick={carregarRelatorio}
                      disabled={relLoading}>
                      {relLoading?<span className="spinner-border spinner-border-sm"/>
                        :<><i className="bi bi-search me-1"/>Gerar</>}
                    </button>
                  </div>
                </div>
                {!relLoading && !relatorio && (
                  <div style={{textAlign:"center",padding:40,color:"var(--gray-400)"}}>
                    <i className="bi bi-graph-up" style={{fontSize:36,display:"block",marginBottom:8}}/>
                    Selecione o período e clique em Gerar
                  </div>
                )}
                {!relLoading && relatorio && (
                  <div style={{padding:16}}>
                    <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(140px,1fr))",
                      gap:12,marginBottom:16}}>
                      {[
                        {label:"Total entradas",value:relatorio.totais?.totalEntradas||0,cor:"#1D4ED8"},
                        {label:"Visitantes",    value:relatorio.totais?.visitantes||0,   cor:"#7C3AED"},
                        {label:"Prestadores",   value:relatorio.totais?.prestadores||0,  cor:"#C2410C"},
                        {label:"Familiares",    value:relatorio.totais?.familiares||0,   cor:"#166534"},
                        {label:"Entregas",      value:relatorio.totais?.entregas||0,     cor:"#0891B2"},
                        {label:"Dentro agora",  value:relatorio.totais?.dentroAgora||0,  cor:"#854D0E"},
                      ].map(k=>(
                        <div key={k.label} className="card" style={{padding:"12px 14px"}}>
                          <div style={{fontSize:24,fontWeight:700,color:k.cor}}>{k.value}</div>
                          <div style={{fontSize:11,color:"var(--gray-400)"}}>{k.label}</div>
                        </div>
                      ))}
                    </div>
                    {relatorio.totais && (
                      <div className="card" style={{padding:16,marginBottom:16}}>
                        <div style={{fontWeight:700,fontSize:13,marginBottom:12}}>Distribuição por tipo</div>
                        {[
                          {label:"Visitantes", value:relatorio.totais.visitantes||0, cor:"#1D4ED8"},
                          {label:"Prestadores",value:relatorio.totais.prestadores||0,cor:"#7C3AED"},
                          {label:"Familiares", value:relatorio.totais.familiares||0, cor:"#166534"},
                          {label:"Entregas",   value:relatorio.totais.entregas||0,   cor:"#C2410C"},
                        ].map(item=>{
                          const total=relatorio.totais.totalEntradas||1;
                          const pct=Math.round((item.value/total)*100);
                          return (
                            <div key={item.label} style={{marginBottom:8}}>
                              <div style={{display:"flex",justifyContent:"space-between",fontSize:12,marginBottom:3}}>
                                <span style={{color:"var(--gray-600)"}}>{item.label}</span>
                                <span style={{fontWeight:700}}>{item.value} ({pct}%)</span>
                              </div>
                              <div style={{height:8,background:"var(--gray-100)",borderRadius:4,overflow:"hidden"}}>
                                <div style={{height:"100%",width:`${pct}%`,background:item.cor,borderRadius:4}}/>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                    {(relatorio.porUnidade||[]).length>0 && (
                      <div className="card" style={{padding:0,overflow:"hidden"}}>
                        <div style={{padding:"10px 14px",fontWeight:700,fontSize:13,
                          background:"var(--gray-50)",borderBottom:"1px solid var(--gray-100)"}}>
                          Top unidades — mais visitadas
                        </div>
                        {relatorio.porUnidade.map((u,i)=>(
                          <div key={i} style={{padding:"9px 14px",display:"flex",
                            justifyContent:"space-between",alignItems:"center",
                            borderBottom:"1px solid var(--gray-50)",fontSize:13}}>
                            <span>Un. {u.nmUnidade}{u.bloco?`/Bl.${u.bloco}`:""}</span>
                            <div style={{display:"flex",alignItems:"center",gap:10}}>
                              <div style={{width:80,height:6,background:"var(--gray-100)",borderRadius:3,overflow:"hidden"}}>
                                <div style={{height:"100%",
                                  width:`${Math.round((u.qtd/(relatorio.porUnidade[0]?.qtd||1))*100)}%`,
                                  background:"var(--primary)",borderRadius:3}}/>
                              </div>
                              <span style={{fontWeight:700,color:"var(--primary)"}}>{u.qtd}</span>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </>
            )}

            {/* ── RELATÓRIO ENCOMENDAS ── */}
            {aba==="enc_relatorio" && (
              <>
                <div style={{padding:"14px 18px",borderBottom:"1px solid var(--gray-100)"}}>
                  <div style={{display:"flex",gap:10,alignItems:"flex-end",flexWrap:"wrap"}}>
                    <div>
                      <label style={{fontSize:12,fontWeight:600,display:"block",marginBottom:4}}>De</label>
                      <input type="date" className="form-control form-control-sm"
                        value={encDtIni} onChange={e=>setEncDtIni(e.target.value)}/>
                    </div>
                    <div>
                      <label style={{fontSize:12,fontWeight:600,display:"block",marginBottom:4}}>Até</label>
                      <input type="date" className="form-control form-control-sm"
                        value={encDtFim} onChange={e=>setEncDtFim(e.target.value)}/>
                    </div>
                    <button className="btn btn-primary btn-sm" onClick={carregarEncRelatorio}
                      disabled={encLoading}>
                      {encLoading?<span className="spinner-border spinner-border-sm"/>
                        :<><i className="bi bi-search me-1"/>Gerar</>}
                    </button>
                  </div>
                </div>
                {!encLoading && encRelatorio===null && (
                  <div style={{textAlign:"center",padding:40,color:"var(--gray-400)"}}>
                    <i className="bi bi-box-seam" style={{fontSize:36,display:"block",marginBottom:8}}/>
                    Selecione o período e clique em Gerar
                  </div>
                )}
                {!encLoading && encRelatorio!==null && (
                  <div>
                    <div style={{padding:16,display:"grid",
                      gridTemplateColumns:"repeat(auto-fill,minmax(140px,1fr))",gap:12,marginBottom:0}}>
                      {[
                        {label:"Total recebidas",value:encRelatorio.length,                                       cor:"#1D4ED8"},
                        {label:"Retiradas",      value:encRelatorio.filter(e=>e.status==="Retirada").length,      cor:"#166534"},
                        {label:"Aguardando",     value:encRelatorio.filter(e=>e.status==="Aguardando").length,    cor:"#C2410C"},
                      ].map(k=>(
                        <div key={k.label} className="card" style={{padding:"12px 14px"}}>
                          <div style={{fontSize:24,fontWeight:700,color:k.cor}}>{k.value}</div>
                          <div style={{fontSize:11,color:"var(--gray-400)"}}>{k.label}</div>
                        </div>
                      ))}
                    </div>
                    {encRelatorio.length===0?(
                      <div style={{textAlign:"center",padding:30,color:"var(--gray-400)"}}>Nenhuma encomenda no período</div>
                    ):(
                      <div style={{overflowX:"auto"}}>
                        <table style={{width:"100%",borderCollapse:"collapse",fontSize:13,marginTop:16}}>
                          <thead>
                            <tr style={{background:"var(--gray-50)"}}>
                              {["Descrição","Unidade","Remetente","Chegou","Retirado por","Status"].map(h=>(
                                <th key={h} style={{padding:"9px 14px",textAlign:"left",
                                  fontWeight:600,color:"var(--gray-600)",fontSize:12,
                                  borderBottom:"1px solid var(--gray-100)"}}>{h}</th>
                              ))}
                            </tr>
                          </thead>
                          <tbody>
                            {encRelatorio.map((e,i)=>(
                              <tr key={e.id} style={{borderBottom:"1px solid var(--gray-100)",
                                background:i%2===0?"#fff":"var(--gray-50)"}}>
                                <td style={{padding:"9px 14px",fontWeight:600}}>{e.descricao}</td>
                                <td style={{padding:"9px 14px"}}>Un. {e.nmUnidade}{e.bloco?`/Bl.${e.bloco}`:""}</td>
                                <td style={{padding:"9px 14px",color:"var(--gray-600)"}}>{e.remetente||e.transportadora||"—"}</td>
                                <td style={{padding:"9px 14px",color:"var(--gray-600)"}}>
                                  {e.dtChegada?new Date(e.dtChegada).toLocaleDateString("pt-BR"):"—"}
                                </td>
                                <td style={{padding:"9px 14px",color:"var(--gray-600)"}}>{e.nmRetiradoPor||"—"}</td>
                                <td style={{padding:"9px 14px"}}>
                                  <span style={{fontSize:11,fontWeight:700,padding:"2px 8px",borderRadius:8,
                                    background:e.status==="Retirada"?"#DCFCE7":e.status==="Aguardando"?"#FFF7ED":"#F1F5F9",
                                    color:e.status==="Retirada"?"#166534":e.status==="Aguardando"?"#C2410C":"#64748B"}}>
                                    {e.status}
                                  </span>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                )}
              </>
            )}

          </div>
        </>
      )}
    </div>
  );
}
